// apps/web/src/pages/author/sections/Instructions.tsx
//
// Phase 8 Plan 08 Task 2 — Instructions form section (authr-01 / authr-03).
//
// recipe.instructions: Instruction[]   each: { text: string }
//
// SCHEMA NOTE: the Instruction schema is intentionally minimal (Phase 1 D-08
// + CONTEXT Deferred Ideas AI2-02). Only `text` exists; no `step` field, no
// `note` field, no markdown. The plan asked for "optional step number Input"
// — that field is NOT on the schema and would fail strictObject parse, so
// we ship only the `text` Textarea here. Plan documents this deviation.
//
// authr-03: field.id keys (enforced by Plan 01 grep gate).
// authr-06: per-path useWatch only; no formState reads.

import type { RecipeDefinitionV1 } from "@the-recipe-spread/shared";
import { useFieldArray, useFormContext } from "react-hook-form";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { FieldArrayRow } from "../FieldArrayRow";

const NEW_INSTRUCTION_DEFAULTS = { text: "" };

export function Instructions(): React.ReactElement {
  const { control } = useFormContext<RecipeDefinitionV1>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "instructions",
  });

  return (
    <Fieldset legend="INSTRUCTIONS">
      {fields.length === 0 ? (
        <EmptyState
          title="No instructions yet."
          cta={{
            label: "Add instruction",
            onClick: () => append(NEW_INSTRUCTION_DEFAULTS),
          }}
        />
      ) : (
        <>
          {fields.map((field, index) => (
            <FieldArrayRow
              key={field.id}
              singular="instruction"
              index={index}
              canMoveUp={index > 0}
              canMoveDown={index < fields.length - 1}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
              onRemove={() => remove(index)}
              deleteDialogTitle="Delete instruction?"
              deleteDialogBody="This instruction step will be removed and remaining steps will renumber."
              deleteConfirmLabel="Delete instruction"
            >
              <FormField
                control={control}
                name={`instructions.${index}.text`}
                render={({ field }) => (
                  <FormItem data-field-path={`instructions.${index}.text`}>
                    <FormLabel>{`Step ${index + 1}`}</FormLabel>
                    <FormControl>
                      <Textarea {...field} autoFocus={!field.value} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FieldArrayRow>
          ))}
          <button
            type="button"
            onClick={() => append(NEW_INSTRUCTION_DEFAULTS)}
            className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Add instruction
          </button>
        </>
      )}
    </Fieldset>
  );
}
Instructions.displayName = "Instructions";
